/**
 * 🔧 FIXED OTP Authentication Service
 * 
 * This service implements the correct OTP flow with matching type parameters
 * between send-otp and verify-otp requests.
 */

// API Base URL
import { getApiBaseUrl } from '@/config/environment';


// ✅ EXACT type constants - DO NOT CHANGE THESE
export const OTP_TYPES = {
  REGISTRATION: 'registration',
  LOGIN: 'login',
  PASSWORD_RESET: 'password_reset'
} as const;

export type OTPType = typeof OTP_TYPES[keyof typeof OTP_TYPES];

// Request/Response interfaces
export interface SendOTPRequest {
  phone: string;
  type: OTPType;
}

export interface SendOTPResponse {
  success: boolean;
  message: string;
  data: {
    phone: string;
    type: string;
    expiresIn: number;
  };
}

export interface VerifyOTPRequest {
  phone: string;
  otp: string;
  type: OTPType; // MUST match the type used in send-otp
}

export interface VerifyOTPResponse {
  success: boolean;
  message: string;
  data: {
    user?: any;
    token?: string;
    refreshToken?: string;
  };
}

export interface OTPAuthError {
  success: false;
  error: string;
  details?: any;
}

class OTPAuthService {
  private currentOTPType: OTPType | null = null;
  private currentPhone: string | null = null;

  /**
   * Send OTP to phone number
   * 🚨 CRITICAL: Store the type to ensure matching in verify
   */
  async sendOTP(request: SendOTPRequest): Promise<SendOTPResponse> {
    try {

      // Store current request details for verification matching
      this.currentOTPType = request.type;
      this.currentPhone = request.phone;

      const response = await fetch(`${getApiBaseUrl()}/api/v1/auth/send-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(request),
      });

      const result = await response.json();

      if (!response.ok) {
        console.error('❌ Send OTP failed:', result);
        throw new Error(result.error || 'Failed to send OTP');
      }

      return result;
    } catch (error) {
      console.error('❌ Send OTP error:', error);
      throw error;
    }
  }

  /**
   * Verify OTP code
   * 🚨 CRITICAL: Uses the SAME type as the send-otp request
   */
  async verifyOTP(request: VerifyOTPRequest): Promise<VerifyOTPResponse> {
    try {

      // Validate that type matches the send request
      if (this.currentOTPType && request.type !== this.currentOTPType) {
      }

      if (this.currentPhone && request.phone !== this.currentPhone) {
      }

      const response = await fetch(`${getApiBaseUrl()}/api/v1/auth/verify-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(request),
      });

      const result = await response.json();

      if (!response.ok) {
        console.error('❌ Verify OTP failed:', result);
        throw new Error(result.error || 'Failed to verify OTP');
      }


      // Clear stored values after successful verification
      this.currentOTPType = null;
      this.currentPhone = null;

      return result;
    } catch (error) {
      console.error('❌ Verify OTP error:', error);
      throw error;
    }
  }

  /**
   * Complete registration flow
   * 🎯 Uses consistent 'registration' type for both requests
   */
  async registerWithOTP(phone: string): Promise<{
    sendOTP: () => Promise<SendOTPResponse>;
    verifyOTP: (otp: string) => Promise<VerifyOTPResponse>;
  }> {
    const otpType = OTP_TYPES.REGISTRATION;

    return {
      sendOTP: () => this.sendOTP({ phone, type: otpType }),
      verifyOTP: (otp: string) => this.verifyOTP({ phone, otp, type: otpType })
    };
  }

  /**
   * Complete login flow  
   * 🎯 Uses consistent 'login' type for both requests
   */
  async loginWithOTP(phone: string): Promise<{
    sendOTP: () => Promise<SendOTPResponse>;
    verifyOTP: (otp: string) => Promise<VerifyOTPResponse>;
  }> {
    const otpType = OTP_TYPES.LOGIN;

    return {
      sendOTP: () => this.sendOTP({ phone, type: otpType }),
      verifyOTP: (otp: string) => this.verifyOTP({ phone, otp, type: otpType })
    };
  }

  /**
   * Complete password reset flow
   * 🎯 Uses consistent 'password_reset' type for both requests
   */
  async resetPasswordWithOTP(phone: string): Promise<{
    sendOTP: () => Promise<SendOTPResponse>;
    verifyOTP: (otp: string) => Promise<VerifyOTPResponse>;
  }> {
    const otpType = OTP_TYPES.PASSWORD_RESET;

    return {
      sendOTP: () => this.sendOTP({ phone, type: otpType }),
      verifyOTP: (otp: string) => this.verifyOTP({ phone, otp, type: otpType })
    };
  }

  /**
   * Get current stored OTP context (for debugging)
   */
  getCurrentContext(): { phone: string | null; type: OTPType | null } {
    return {
      phone: this.currentPhone,
      type: this.currentOTPType
    };
  }

  /**
   * Clear stored OTP context
   */
  clearContext(): void {
    this.currentOTPType = null;
    this.currentPhone = null;
  }
}

// Export singleton instance
export const otpAuthService = new OTPAuthService();

// Example usage functions for the frontend team:

/**
 * 📝 EXAMPLE: Registration Flow
 */
export async function handleRegistration(phoneNumber: string, otpCode: string) {
  try {
    const auth = await otpAuthService.registerWithOTP(phoneNumber);
    
    // Step 1: Send OTP
    const sendResult = await auth.sendOTP();
    
    // Step 2: Verify OTP (after user enters code)
    const verifyResult = await auth.verifyOTP(otpCode);
    
    return verifyResult;
  } catch (error) {
    console.error('Registration failed:', error);
    throw error;
  }
}

/**
 * 📝 EXAMPLE: Login Flow
 */
export async function handleLogin(phoneNumber: string, otpCode: string) {
  try {
    const auth = await otpAuthService.loginWithOTP(phoneNumber);
    
    // Step 1: Send OTP
    const sendResult = await auth.sendOTP();
    
    // Step 2: Verify OTP (after user enters code)
    const verifyResult = await auth.verifyOTP(otpCode);
    
    return verifyResult;
  } catch (error) {
    console.error('Login failed:', error);
    throw error;
  }
}

/**
 * 📝 EXAMPLE: Password Reset Flow
 */
export async function handlePasswordReset(phoneNumber: string, otpCode: string) {
  try {
    const auth = await otpAuthService.resetPasswordWithOTP(phoneNumber);
    
    // Step 1: Send OTP
    const sendResult = await auth.sendOTP();
    
    // Step 2: Verify OTP (after user enters code)
    const verifyResult = await auth.verifyOTP(otpCode);
    
    return verifyResult;
  } catch (error) {
    console.error('Password reset failed:', error);
    throw error;
  }
}